<i18n>
{
    "en_US": {
        "edit": "Edit",
        "add": "Add",
        "cancel": "Cancel",
        "alert-old-note": "Current note version seems old - you should refresh the page",
        "invalid-url": "Please enter a valid URL",
        "markdown-bold": "bold text here",
        "markdown-code": "code here",
        "markdown-header": "heading text here",
        "markdown-image": "Please enter the image URL",
        "markdown-image-description": "enter image description here",
        "markdown-image-title-here": " \"enter image title here\")",
        "markdown-italic": "emphasized text here",
        "markdown-link": "Please enter the link URL",
        "markdown-link-description": "enter link description here",
        "markdown-list": "list text here",
        "markdown-quote": "quote here"
    },
    "fr_FR": {
        "edit": "Modifier",
        "add": "Ajouter",
        "cancel": "Annuler",
        "alert-old-note": "La version actuelle de la note semble ancienne - vous devriez rafraichir la page",
        "invalid-url": "Veuillez entrer une URL valide",
        "markdown-bold": "texte en gras ici",
        "markdown-code": "code ici",
        "markdown-header": "titre ici",
        "markdown-image": "Veuillez entrer l'URL de l'image",
        "markdown-image-description": "entrez la description de l'image ici",
        "markdown-image-title-here": " \"entrez le titre de l'image ici\")",
        "markdown-italic": "texte en italique ici",
        "markdown-link": "Veuillez entrer l'URL du lien",
        "markdown-link-description": "entrez la description du lien ici",
        "markdown-list": "liste ici",
        "markdown-quote": "citation ici"
    }
}
</i18n>

<template>
    <div class="ace-editor-container">
        <toolbar />
        <p class="alert-old-note" v-if="isOldNote" v-text="$t('alert-old-note')"></p>
        <overlay-upload-container className="ace-editor-preview">
            <template slot="content">
                <editor v-model="selectedNote.content" @init="editorInit" lang="markdown" theme="chrome" height="500px" ref="editor" />
                <markdown-preview-split />
            </template>
        </overlay-upload-container>
        <markdown-preview-full />
        <flash-basic />
        <flash-upload />
        <modal-email />
        <modal-upload />
        <modal-user />
        <overlay-single-note-loading />
        <markdown-controls-toolbar inverted />
    </div>
</template>

<script>
    import store from "store/store";
    import FlashBasic from "flashes/Basic";
    import FlashUpload from "flashes/Upload";
    import Toolbar from "components/application-specific/note/Toolbar";
    import MarkdownControlsToolbar from "components/application-specific/note/markdown/controls/Toolbar";
    import OverlaySingleNoteLoading from "overlays/SingleNoteLoading";
    import OverlayUploadContainer from "overlays/upload/Container";
    import MarkdownPreviewSplit from "components/application-specific/note/markdown/previews/Split";
    import MarkdownPreviewFull from "components/application-specific/note/markdown/previews/Full";
    import ModalEmail from "modals/Email";
    import ModalUpload from "modals/Upload";
    import ModalUser from "modals/User";
    import event from "plugins/event";
    import { mapState, mapActions } from "vuex";

    export default {
        name: "NoteEditor",
        store: store,
        data: function() {
            return {
                originalSelectedNote: null,
                content: "",
                autoRefreshLastUpdateTimer: null,
                editor: null,
                scrollingEditor: false,
                scrollingPreview: false,
                lastUploadCursorPosition: null
            };
        },
        computed: {
            isOldNote: function() {
                return this.originalSelectedNote && this.originalSelectedNote.id && this.lastSelectedUpdate && new Date(this.lastSelectedUpdate) > new Date(this.originalSelectedNote.updated_at || this.originalSelectedNote.created_at);
            },
            ...mapState("note", ["selectedNote", "lastSelectedUpdate"]),
            ...mapState("application", ["isMarkdownSplitVisible"])
        },
        watch: {
            "$route": {
                deep: true,
                handler: function(newPage) {
                    // We cancel ALL uploads currently happening
                    store.commit("upload/removeAllUploads");
                    // We load *or not* the note
                    const noteId = newPage.params.note_id;
                    if (noteId) {
                        store.dispatch("note/loadSingleNote", noteId)
                        .catch(function() {});
                    } else {
                        store.commit("note/eraseSelectedNote");
                    }
                }
            },
            selectedNote: function(newValue) {
                const previousId = this.originalSelectedNote ? this.originalSelectedNote.id : undefined;
                this.originalSelectedNote = JSON.parse(JSON.stringify(newValue));
                if (previousId !== newValue.id) {
                    this.focusOnEditor();
                    this.resetScroll();
                }
                this.$Progress.finish();

                // We are in edit mode, not new note creation
                if (this.originalSelectedNote.id) {
                    document.title = this.$t("edit") + " \"" + this.originalSelectedNote.title + "\" | " + process.env.VUE_APP_TITLE;
                }
            },
            isMarkdownSplitVisible: function(newValue) {
                const container = this.getEditor().container;
                if (newValue === true) {
                    container.style.width = "50%";
                } else {
                    container.style.width = "100%";
                }
                // We make sure resize happen
                setTimeout(function() {
                    this.getEditor().resize();
                }.bind(this), 200);
                setTimeout(function() {
                    this.getEditor().resize();
                }.bind(this), 500);
                setTimeout(function() {
                    this.getEditor().resize();
                }.bind(this), 1000);
            }
        },
        components: {
            Toolbar,
            MarkdownControlsToolbar,
            FlashBasic,
            FlashUpload,
            OverlaySingleNoteLoading,
            OverlayUploadContainer,
            MarkdownPreviewSplit,
            MarkdownPreviewFull,
            ModalEmail,
            ModalUpload,
            ModalUser,
            Editor: require("vue2-ace-editor")
        },
        beforeRouteEnter: function(to, from, next) {
            const noteId = to.params.note_id;
            if (noteId) {
                store.dispatch("note/loadSingleNote", noteId)
                .catch(function() {});
            } else {
                store.commit("note/eraseSelectedNote");
            }
            next();
        },
        methods: {
            editorInit: function() {
                require("brace/ext/language_tools");
                require("brace/mode/markdown");
                require("brace/theme/chrome");
                require("brace/snippets/markdown");
            },
            getEditor: function() {
                return this.$refs.editor.editor;
            },
            getEditorHeight: function() {
                const root = $(this.$el);
                return root.find(".ace_editor").height() - root.find(".ace_scrollbar.ace_scrollbar-h").height();
            },
            focusOnEditor: function() {
                this.$refs.editor.editor.focus();
            },

            isTextSelected: function(range) {
                return range.start.row !== range.end.row ||
                    range.start.column !== range.end.column;
            },
            shift: function(range, type, offset) {
                let d = jQuery.extend(true, {}, range);
                d[type] += offset;
                return d;
            },
            insertIntelligentMarkers: function(text, marker) {
                var editor = this.getEditor(),
                    range = editor.getSelectionRange(),
                    session = editor.getSession(),
                    l = marker.length;
                if (this.isTextSelected(range)) {
                    // If we have "**" before AND after, then we remove bold (intelligent)...
                    var b = {
                            start: this.shift(range.start, "column", -l),
                            end: range.start
                        },
                        a = {
                            start: range.end,
                            end: this.shift(range.end, "column", l)
                        },
                        before = editor.session.getTextRange(b),
                        after  = editor.session.getTextRange(a);

                    if (before === marker && after === marker) {
                        session.remove(a);
                        session.remove(b);
                    } else {
                        session.insert(range.end, marker);
                        session.insert(range.start, marker);
                        editor.getSession().selection.setSelectionRange({
                            start: this.shift(range.start, "column", l),
                            end: this.shift(range.end, "column", l)
                        });
                    }
                } else {
                    var el = {
                        start: this.shift(editor.selection.getCursor(), "column", l),
                        end: this.shift(editor.selection.getCursor(), "column", text.length + l)
                    };
                    editor.insert(marker + text + marker);
                    editor.getSession().selection.setSelectionRange(el);
                }
            },
            insertIntelligentLeftMarker: function(text, marker, alsoTestTrim) {
                var editor = this.getEditor(),
                    range = editor.getSelectionRange(),
                    session = editor.getSession(),
                    l = marker.length;
                if (this.isTextSelected(range)) {
                    // If we have "**" before AND after, then we remove bold (intelligent)...
                    var b = {
                            start: this.shift(range.start, "column", -l),
                            end: range.start
                        },
                        before = editor.session.getTextRange(b);

                    if (before === marker || (alsoTestTrim === true && before === marker.trim())) {
                        session.remove(b);
                    } else {
                        session.insert(range.start, marker);
                        editor.getSession().selection.setSelectionRange({
                            start: this.shift(range.start, "column", l),
                            end: this.shift(range.end, "column", l)
                        });
                    }
                } else {
                    var el = {
                        start: this.shift(editor.selection.getCursor(), "column", l),
                        end: this.shift(editor.selection.getCursor(), "column", text.length + l)
                    };
                    editor.insert(marker + text);
                    editor.getSession().selection.setSelectionRange(el);
                }
            },
            insertLeftMarker: function(text, marker) {
                var editor = this.getEditor(),
                    range = editor.getSelectionRange(),
                    session = editor.getSession(),
                    l = marker.length;
                if (this.isTextSelected(range)) {
                    session.insert(range.start, marker);
                    editor.getSession().selection.setSelectionRange({
                        start: this.shift(range.start, "column", l),
                        end: this.shift(range.end, "column", l)
                    });
                } else {
                    var el = {
                        start: this.shift(editor.selection.getCursor(), "column", l),
                        end: this.shift(editor.selection.getCursor(), "column", text.length + l)
                    };
                    editor.insert(marker + text);
                    editor.getSession().selection.setSelectionRange(el);
                }
            },
            insertRawImageMarker: function(comment, lmarker, m1marker, m2marker, rmarker, text) {
                var editor = this.getEditor(),
                    range = editor.getSelectionRange(),
                    session = editor.getSession(),
                    ll = lmarker.length,
                    m1l = m1marker.length;

                if (this.isTextSelected(range)) {
                    session.insert(range.end, m2marker + text + rmarker);
                    session.insert(range.start, lmarker);
                    editor.getSession().selection.setSelectionRange({
                        start: this.shift(range.start, "column", ll),
                        end: this.shift(range.start, "column", ll + text.length)
                    });
                } else {
                    var el = {
                        start: this.shift(editor.selection.getCursor(), "column", ll),
                        end: this.shift(editor.selection.getCursor(), "column", ll + m1l)
                    };
                    editor.insert(lmarker + m1marker + m2marker + text + rmarker);
                    editor.getSession().selection.setSelectionRange(el);
                }
                editor.focus();
                return true;
            },
            insertImageMarker: function(comment, lmarker, m1marker, m2marker, rmarker) {
                swal({
                    title: "",
                    text: comment,
                    type: "input",
                    showCancelButton: true,
                    closeOnConfirm: true,
                    inputPlaceholder: "http://",
                    inputValue: "http://",
                    confirmButtonText: this.$t("add"),
                    cancelButtonText: this.$t("cancel"),
                }, function (inputValue) {
                    if (inputValue === false) return false;
                    if (inputValue === "") {
                        swal.showInputError(this.$t("invalid-url"));
                        return false
                    }

                    this.insertRawImageMarker(comment, lmarker, m1marker, m2marker, rmarker, inputValue);
                }.bind(this));
            },

            insertBold: function() {
                this.insertIntelligentMarkers(this.$t("markdown-bold"), "**");
                this.focusOnEditor();
            },
            insertItalic: function() {
                this.insertIntelligentMarkers(this.$t("markdown-italic"), "_");
                this.focusOnEditor();
            },
            insertHeader: function() {
                this.insertIntelligentLeftMarker(this.$t("markdown-header"), "## ", true);
                this.focusOnEditor();
            },
            insertRawLink: function(url) {
                var editor = this.getEditor(),
                    currentCursorPosition = editor.getCursorPosition(),
                    hasSavedPosition = false;

                if (this.lastUploadCursorPosition) {
                    hasSavedPosition = true;
                    this.getEditor().getSelection().moveTo(
                        this.lastUploadCursorPosition.row,
                        this.lastUploadCursorPosition.column
                    );
                }

                this.insertRawImageMarker(this.$t("markdown-link"), "[",
                    this.$t("markdown-link-description"), "](", ")", url);

                if (hasSavedPosition) {
                    this.lastUploadCursorPosition = null;
                    this.getEditor().getSelection().moveTo(
                        currentCursorPosition.row,
                        currentCursorPosition.column
                    );
                }

                this.focusOnEditor();
            },
            insertRawImage: function(url) {
                var editor = this.getEditor(),
                    currentCursorPosition = editor.getCursorPosition(),
                    hasSavedPosition = false;

                if (this.lastUploadCursorPosition) {
                    hasSavedPosition = true;
                    this.getEditor().getSelection().moveTo(
                        this.lastUploadCursorPosition.row,
                        this.lastUploadCursorPosition.column
                    );
                }

                this.insertRawImageMarker(this.$t("markdown-image"), "![",
                    this.$t("markdown-image-description"), "](",
                    this.$t("markdown-image-title-here"), url);

                if (hasSavedPosition) {
                    this.lastUploadCursorPosition = null;
                    this.getEditor().getSelection().moveTo(
                        currentCursorPosition.row,
                        currentCursorPosition.column
                    );
                }

                this.focusOnEditor();
            },
            insertUpload: function(file) {
                const mediaMimeTypes = [
                    "audio/aac",
                    "audio/midi",
                    "audio/x-midi",
                    "audio/ogg",
                    "audio/wav",
                    "audio/webm",
                    "audio/3gpp",
                    "audio/3gpp2",

                    "video/ogg",
                    "application/ogg",
                    "video/mpeg",
                    "video/x-msvideo",
                    "video/webm",
                    "video/3gpp",
                    "video/3gpp2",

                    "image/bmp",
                    "image/gif",
                    "image/x-icon",
                    "image/jpeg",
                    "image/png",
                    "image/tiff",
                    "image/x-tiff",
                    "image/webp"
                ];

                if (mediaMimeTypes.indexOf(file.type) >= 0) {
                    event.$emit("upload-insert-image", file.url);
                } else {
                    event.$emit("upload-insert-link", file.url);
                }
            },
            insertTable: function(dimensions) {
                const editor = this.getEditor();

                if (dimensions.rows <= 0 || dimensions.colums <= 0) {
                    return;
                }

                const cells = [];
                const maxs = [];

                // Generating content
                for (let a = 0, b = dimensions.rows + 2; a < b; a++) {
                    if (a === 0) {
                        const headers = [];
                        const spaces = [];
                        for (let c = 0, d = dimensions.columns; c < d; c++) {
                            headers.push("Columns #" + (c + 1));
                            spaces.push(":---");
                        }
                        cells.push(headers);
                        cells.push(spaces);

                        a++;
                    } else {
                        const row = [];
                        for (let c = 0, d = dimensions.columns; c < d; c++) {
                            row.push("Cell " + (a - 1) + "," + (c + 1));
                        }
                        cells.push(row);
                    }
                }

                // Getting max of each columns
                for (let a = 0, b = cells[0].length; a < b; a++) {
                    let max = -1;
                    for (let c = 0, d = cells.length; c < d; c++) {
                        if (cells[c][a].length > max) {
                            max = cells[c][a].length;
                        }
                    }
                    maxs.push(max);
                }

                // Now we add spaces at the end of each cells that needs it
                for (let a = 0, b = cells.length; a < b; a++) {
                    for (let c = 0, d = cells[a].length; c < d; c++) {
                        // We fill with spaces
                        while (cells[a][c].length < maxs[c]) {
                            cells[a][c] += " ";
                        }
                    }
                }

                // We render into the editor
                editor.insert("\n");
                for (let a = 0, b = cells.length; a < b; a++) {
                    editor.insert("| " + cells[a].join(" | ") + " |\n");
                }
            },
            insertListUl: function() {
                this.insertLeftMarker(this.$t("markdown-list"), "- ");
                this.focusOnEditor();
            },
            insertListOl: function() {
                this.insertLeftMarker(this.$t("markdown-list"), "1. ");
                this.focusOnEditor();
            },
            insertListTodo: function() {
                this.insertLeftMarker(this.$t("markdown-list"), "- [x] ");
                this.focusOnEditor();
            },
            insertCode: function() {
                this.insertIntelligentMarkers(this.$t("markdown-code"), "```");
                this.focusOnEditor();
            },
            insertQuote: function() {
                this.insertLeftMarker(this.$t("markdown-quote"), "> ");
                this.focusOnEditor();
            },

            resetScroll: function() {
                $(".markdown-split-preview").get(0).scrollTop = 0;
                const editor = this.getEditor();
                editor.session.setScrollTop(0);
                editor.renderer.updateFull(true);
            },
            editorScrollEvent: function(position) {
                if (this.scrollingPreview === true) {
                    return;
                }
                this.scrollingEditor = true;
                const editor = this.getEditor(),
                      el = $(this.$el).find(".markdown-split-preview").get(0),
                      editorHeight = editor.session.getScreenLength() * editor.renderer.lineHeight,
                      height = this.getEditorHeight(),
                      percentage = position / (editorHeight - height);

                el.scrollTop = percentage * (el.scrollHeight - el.offsetHeight);

                setTimeout(function() {
                    this.scrollingEditor = false;
                }.bind(this), 5);
            },
            previewScrollEvent: function(e) {
                if (this.scrollingEditor === true) {
                    return;
                }
                var that = e.currentTarget;
                this.scrollingPreview = true;
                const editor = this.getEditor(),
                      percentage = that.scrollTop / (that.scrollHeight - that.offsetHeight),
                      height = this.getEditorHeight(),
                      editorHeight = editor.session.getScreenLength() * editor.renderer.lineHeight;

                editor.session.setScrollTop(percentage * (editorHeight - height));

                setTimeout(function() {
                    this.scrollingPreview = false;
                }.bind(this), 5);
            },
            bindKeyboardSaveEvent: function(e) {
                var key = undefined;
                var possible = [ e.key, e.keyIdentifier, e.keyCode, e.which ];

                while (key === undefined && possible.length > 0) {
                    key = possible.pop();
                }

                if (key && (key == "115" || key == "83") && (e.ctrlKey || e.metaKey) && !(e.altKey)) {
                    e.preventDefault();
                    this.save();
                    return false;
                }
                return true;
            },
            save: function() {
                // Edit a note
                if (this.selectedNote && this.selectedNote.id) {
                    // The progress bar is disabled for this one...
                    this.editNote(this.selectedNote)
                    .then(function() {})
                    .catch(function() {});
                }
                // Create a note
                else {
                    this.createNote(this.selectedNote)
                    .then(function(response) {
                        this.$Progress.finish();
                        this.$router.push({
                            name: "home.filled-note-editor",
                            params: {
                                note_id: response.data.id
                            }
                        });
                    }.bind(this))
                    .catch(function() {});
                }
            },
            refreshLastUpdate: function() {
                if (this.selectedNote && this.selectedNote.id) {
                    this.lastNoteUpdated(this.selectedNote)
                    .then(function() {
                        this.$Progress.finish();
                    }.bind(this))
                    .catch(function() {});
                }
            },
            saveCursorPosition: function() {
                this.lastUploadCursorPosition = this.getEditor().getCursorPosition();
            },
            ...mapActions("note", ["createNote", "editNote", "lastNoteUpdated"])
        },
        mounted: function() {
            // Markdown controls events
            event.$on("markdown-insert-bold", this.insertBold);
            event.$on("markdown-insert-italic", this.insertItalic);
            event.$on("markdown-insert-header", this.insertHeader);
            event.$on("markdown-insert-table", this.insertTable);
            event.$on("markdown-insert-list-ul", this.insertListUl);
            event.$on("markdown-insert-list-ol", this.insertListOl);
            event.$on("markdown-insert-list-todo", this.insertListTodo);
            event.$on("markdown-insert-code", this.insertCode);
            event.$on("markdown-insert-quote", this.insertQuote);

            // Uploader uploaded an image (or document)
            event.$on("upload", this.insertUpload);
            event.$on("upload-insert-image", this.insertRawImage);
            event.$on("upload-insert-link", this.insertRawLink);

            // Mark position
            event.$onWithBinding("ace-editor-save-position", this.saveCursorPosition, this);

            // Syncing scroll
            $(".markdown-split-preview").on("scroll", this.previewScrollEvent.bind(this));
            this.getEditor().session.on("changeScrollTop", this.editorScrollEvent.bind(this));

            // Auto-save note
            this.originalSelectedNote = JSON.parse(JSON.stringify(this.selectedNote));
            this.autoRefreshLastUpdateTimer = setInterval(this.refreshLastUpdate.bind(this), process.env.VUE_APP_REFRESH_LAST_UPDATE_MILLISECONDS);

            // Binding keyboard event (ctrl + s or cmd + s)
            // Ugly hack...
            window.__custom__ace__on__key__down = this.bindKeyboardSaveEvent.bind(this);
            $(document).on("keydown", window.__custom__ace__on__key__down);
        },
        beforeDestroy: function() {
            // Markdown controls events
            event.$off("markdown-insert-bold", this.insertBold);
            event.$off("markdown-insert-italic", this.insertItalic);
            event.$off("markdown-insert-header", this.insertHeader);
            event.$off("markdown-insert-table", this.insertTable);
            event.$off("markdown-insert-list-ul", this.insertListUl);
            event.$off("markdown-insert-list-ol", this.insertListOl);
            event.$off("markdown-insert-list-todo", this.insertListTodo);
            event.$off("markdown-insert-code", this.insertCode);
            event.$off("markdown-insert-quote", this.insertQuote);

            // Uploader uploaded an image (or document)
            event.$off("upload", this.insertUpload);
            event.$off("upload-insert-image", this.insertRawImage);
            event.$off("upload-insert-link", this.insertRawLink);

            // Mark position
            event.$offWithBinding("ace-editor-save-position", this.saveCursorPosition, this);

            // Syncing scroll
            $(".markdown-split-preview").off("scroll");
            this.getEditor().session.removeAllListeners("changeScrollTop");

            // Last Refresh
            clearInterval(this.autoRefreshLastUpdateTimer);
            this.autoRefreshLastUpdateTimer = null;

            // Cleaning keyboard event (ctrl + s or cmd + s)
            $(document).off("keydown", window.__custom__ace__on__key__down);
            window.__custom__ace__on__key__down = undefined;
        }
    }
</script>

<style scoped>
    .alert-old-note {
        color: #842029;
        background-color: #f8d7da;
        padding: 1rem 1rem;
    }
    .ace-editor-container {
        min-height: 100%;
        height: 100%;
        position: relative;
    }
    .ace-editor-preview {
        height: calc(100% - 56px) !important;
        max-height: calc(100% - 56px) !important;
        width: 100%;
        position: relative;
    }
    .ace_editor {
        position: absolute;
        height: calc(100%) !important;
        max-height: calc(100%) !important;
        width: 100%;
        left: 0;
        top: 0;
        -webkit-transition: width 0.5s ease-in-out;
        -moz-transition: width 0.5s ease-in-out;
        -ms-transition: width 0.5s ease-in-out;
        -o-transition: width 0.5s ease-in-out;
        transition: width 0.5s ease-in-out;
    }

    /* Flashes specific code */
    .ace-editor-container >>> .flashes {
        position: absolute;
        width: 300px;
        bottom: 10px;
        left: 50%;
        margin-left: -150px;
        z-index: 7;
    }

    /* Uploads specific code */
    .ace-editor-container >>> .uploads {
        position: absolute;
        right: 20px;
        width: 400px;
        bottom: 10px;
        z-index: 7;
    }

    @media screen and (max-width: 850px) {
        .ace-editor-container >>> .flashes {
            bottom: 65px;
        }
        .ace-editor-container >>> .uploads {
            bottom: 65px;
        }
        .ace-editor-preview {
            height: calc(100% - 112px) !important;
            max-height: calc(100% - 112px) !important;
        }
    }

    @media screen and (max-width: 630px) {
        .ace_editor {
            width: 100% !important;
        }
    }
</style>